/* eslint-disable no-case-declarations */
import { CloseCircleOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { useEffect, useReducer, useRef, useState } from 'react'

import { baseApi, useMeQuery } from '@Store/api'
import {
  userIsChannelBasedSelector,
  userIsHybridSelector,
} from '@Store/api/auth'
import {
  useLazyGetImportActivityQuery,
  useLazyGetLatestImportActivityQuery,
} from '@Store/api/product'
import { useAppDispatch, useAppSelector } from '@Store/hooks'
import type { PaginatedQueryParamsWithCachePreference } from '@Store/types'

import RecentImportsTable from './RecentImportsTable'
import {
  type StatusContent,
  getBatchImportFullMessage,
} from '../../constants/messages'
import statusMappedUI from '../../constants/statusMappedUI'
import ChannelSummary from '../competitor/ChannelSummary'

const INITIAL_BATCH_IMPORT_VIEW_STATE = {
  newImportDisabled: true,
  latestImportId: undefined,
  latestImportFinished: undefined,
  latestImportFullMessage: undefined,
  hasNoImport: false,
  currentImportState: undefined,
  currentImportFullMessage: undefined,
  currentImportMessageDismissed: false,
  currentImportDone: false,
  currentImportPollingInterval: undefined,
}

function RecentImports({
  type,
}: {
  type: ('apt' | 'upt' | 'upt-c' | 'conveyor')[]
}): JSX.Element {
  const key = useRef(Date.now())

  const isChannelBased = useAppSelector(userIsChannelBasedSelector)
  const isHybrid = useAppSelector(userIsHybridSelector)

  const { data: meData } = useMeQuery()
  const dispatch = useAppDispatch()

  const [batchImportViewState, setBatchImportViewState] = useReducer(
    (
      state: {
        newImportDisabled: boolean
        latestImportId?: number
        latestImportFinished?: boolean
        latestImportFullMessage?: StatusContent
        hasNoImport: boolean
        currentImportState?: string
        currentImportFullMessage?: StatusContent
        currentImportMessageDismissed: boolean
        currentImportDone: boolean
        currentImportPollingInterval?: number
      },
      action: { type?: string; payload?: any },
    ) => {
      switch (action.type) {
        case 'setImportDisabled':
          return {
            ...state,
            newImportDisabled: action.payload,
          }
        case 'setLatestImport':
          const latestImportMessage =
            getBatchImportFullMessage(action.payload?.status) ?? {}
          const latestImportFinished = latestImportMessage?.finished ?? false
          const newImportEnabled = latestImportMessage?.dismissible ?? false

          return {
            ...state,
            latestImportId: action.payload.id,
            latestImportFinished,
            latestImportFullMessage: latestImportMessage,
            hasNoImport: false,
            newImportDisabled: !newImportEnabled,
          }
        case 'setNoImportFound':
          return {
            ...state,
            hasNoImport: !!action.payload,
            ...(action.payload
              ? {
                  latestImportId: undefined,
                  newImportDisabled: false,
                }
              : {}),
          }
        case 'updateCurrentImportStatus':
          const {
            importStatus,
            importMessage,
          }: {
            importStatus?: string
            importMessage?: StatusContent
          } = action.payload

          if (state.latestImportFinished) {
            return {
              ...state,
            }
          }

          const currentImportDone = importMessage?.finished ?? false

          return {
            ...state,
            currentImportState: importStatus ?? undefined,
            currentImportFullMessage: importMessage ?? undefined,
            currentImportMessageDismissed: false,
            currentImportDone,
            ...(currentImportDone
              ? {
                  latestImportFinished: true,
                  newImportDisabled: false,
                  currentImportPollingInterval: undefined,
                }
              : {
                  currentImportPollingInterval: 2000,
                }),
          }
        case 'dismissCurrentImportMessage':
          return {
            ...state,
            currentImportMessageDismissed: true,
          }
        case 'disablePolling':
          return {
            ...state,
            currentImportPollingInterval: undefined,
          }
        case 'reset':
          return INITIAL_BATCH_IMPORT_VIEW_STATE
        default:
          return state
      }
    },
    INITIAL_BATCH_IMPORT_VIEW_STATE,
  )

  const [
    fetchImportList,
    { currentData: importListData, isLoading, isError, error },
  ] = useLazyGetImportActivityQuery()

  const [
    fetchCurrentImport,
    {
      isLoading: isCurrentImportLoading,
      currentData: currentImportData,
      isError: isCurrentImportError,
      error: currentImportError,
    },
  ] = useLazyGetLatestImportActivityQuery({
    pollingInterval: batchImportViewState.currentImportPollingInterval,
  })

  const importStatus = currentImportData?.current_state
  const importMessage = importStatus
    ? getBatchImportFullMessage(importStatus)
    : null

  const handlePaginationChange = (
    pagination: PaginatedQueryParamsWithCachePreference,
    preferCache: boolean,
  ) => {
    const fetchPagination = { ...pagination }
    delete fetchPagination.preferCache

    fetchImportList(
      {
        ...fetchPagination,
        offset: (pagination.page - 1) * pagination.limit,
        type,
      },
      preferCache,
    )
  }

  const onChannelSummaryClose = (): void => {
    setBatchImportViewState({ type: 'disablePolling' })
  }

  useEffect(() => {
    if (batchImportViewState.latestImportId) {
      fetchCurrentImport(batchImportViewState.latestImportId)
    }
  }, [batchImportViewState.latestImportId])

  useEffect(() => {
    if (!importListData?.data) {
      return
    }

    if (importListData?.data?.length > 0) {
      const latestImport = importListData.data[0]

      setBatchImportViewState({
        type: 'setLatestImport',
        payload: {
          id: latestImport.id,
          status: latestImport.current_state,
        },
      })
    } else {
      setBatchImportViewState({
        type: 'setNoImportFound',
        payload: true,
      })
    }
  }, [importListData])

  useEffect(() => {
    if (!currentImportData?.current_state) {
      return
    }

    setBatchImportViewState({
      type: 'updateCurrentImportStatus',
      payload: {
        importStatus,
        importMessage,
      },
    })
  }, [currentImportData])

  useEffect(() => {
    if (isError && (error as any).data?.statusCode === 404) {
      setBatchImportViewState({
        type: 'setNoImportFound',
        payload: true,
      })
    }
  }, [isError, error])

  useEffect(() => {
    if (
      (isCurrentImportError &&
        (currentImportError as any).data?.statusCode === 404) ||
      importMessage?.dismissible
    ) {
      setBatchImportViewState({
        type: 'setImportDisabled',
        payload: false,
      })
    }
  }, [isCurrentImportError, currentImportError])

  useEffect(() => {
    if (batchImportViewState.currentImportDone) {
      dispatch(
        baseApi.util.invalidateTags(
          meData && !meData.has_tracker
            ? ['Products', 'ProductCount', 'Me', 'BatchImport']
            : ['Products', 'ProductCount', 'BatchImport'],
        ),
      )
    }
  }, [batchImportViewState.currentImportDone, dispatch, meData])

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      setBatchImportViewState({ type: 'reset' })
    }
  }, [])

  const showInfoLoading =
    (isCurrentImportLoading || !batchImportViewState.latestImportId) &&
    !batchImportViewState.latestImportFinished &&
    !batchImportViewState.hasNoImport

  const dontShowInfo =
    batchImportViewState?.hasNoImport ||
    batchImportViewState?.currentImportMessageDismissed

  const showChannelSummary =
    !['UPT', 'UPT-C'].includes(
      currentImportData?.operation?.toUpperCase() as string,
    ) &&
    (isChannelBased || isHybrid) &&
    batchImportViewState.latestImportId &&
    !batchImportViewState.currentImportDone &&
    batchImportViewState?.currentImportFullMessage

  const uiFullMessageKey = batchImportViewState?.currentImportFullMessage
    ? batchImportViewState.currentImportFullMessage.status
    : batchImportViewState?.latestImportFullMessage
      ? batchImportViewState.latestImportFullMessage.status
      : null
  const ui = uiFullMessageKey
    ? statusMappedUI[uiFullMessageKey as keyof typeof statusMappedUI]
    : null
  if (ui) {
    ui['description'] = batchImportViewState?.currentImportFullMessage
      ? batchImportViewState?.currentImportFullMessage?.message
      : batchImportViewState?.latestImportFullMessage
        ? batchImportViewState?.latestImportFullMessage?.message
        : null
  }

  return (
    <>
      {showInfoLoading ? (
        <div className="mt-8">
          <div className="border border-solid bg-white-100 rounded-lg">
            <div className="p-4 flex flex-row">
              <div className="animate-pulse bg-gray-200 h-6 w-6 rounded-md"></div>
              <div className="flex flex-col ml-3 w-full">
                <h3 className={`font-medium text-sm`}>
                  <div className="animate-pulse bg-gray-200 h-4 w-20 rounded-md"></div>
                </h3>
                <div className="animate-pulse bg-gray-200 h-4 w-full rounded-md mt-2"></div>
                <div className="animate-pulse bg-gray-200 h-4 w-64 rounded-md mt-1.5"></div>
                <div className="animate-pulse bg-gray-200 h-4 w-24 rounded-md mt-2"></div>
              </div>
            </div>
          </div>
        </div>
      ) : dontShowInfo ? (
        <></>
      ) : (
        <>
          {showChannelSummary ? (
            <ChannelSummary
              isClosable={
                batchImportViewState?.currentImportFullMessage?.dismissible
              }
              importId={batchImportViewState.latestImportId}
              onClose={onChannelSummaryClose}
            />
          ) : (
            <></>
          )}
          {ui ? (
            <div className="mt-8">
              <div
                className={`border border-solid ${ui.borderColor} rounded-lg ${ui.bgColor} relative`}
              >
                <div className="p-4 flex flex-row">
                  {ui.Icon}
                  <div className="flex flex-col w-full ml-3">
                    <h3 className={`font-medium text-sm ${ui.titleColor}`}>
                      {ui.title}
                    </h3>
                    <p className={`font-normal text-sm ${ui.textColor} mt-1`}>
                      {ui.description}
                    </p>
                    <ui.Action />
                  </div>
                </div>
                <Button
                  shape="circle"
                  className={`absolute top-4 right-4 -m-1 p-0 !w-5 !min-w-5 !h-5 border-none ${ui.textColor} bg-transparent`}
                  icon={
                    <CloseCircleOutlined className="[&>svg]:w-5 [&>svg]:h-5" />
                  }
                  onClick={() =>
                    setBatchImportViewState({
                      type: 'dismissCurrentImportMessage',
                    })
                  }
                />
              </div>
            </div>
          ) : (
            <></>
          )}
        </>
      )}
      <RecentImportsTable
        onPaginationChange={handlePaginationChange}
        isLoading={isLoading}
        data={importListData?.data}
        meta={importListData?.meta}
        key={`recent-imports-${key.current}`}
        type={type}
      />
    </>
  )
}

export default RecentImports
